import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { isArray, isUndefined } from 'lodash';
import type { TAppDispatch, TRootState } from 'slices/store';
import { errorNotification } from 'utils/notifications';
import { EAppId } from '@timeedit/types';
import { EAppEnv } from '@timeedit/types/lib/enums/appEnv.enum';
import {
  getRedirectUri,
  removeCloudPrefix,
} from '@timeedit/types/lib/utils/redirectUrls.util';

type ViewerPagesState = {
  hasErrors: boolean;
  pages: ViewerOptions[];
};

const initialState: ViewerPagesState = {
  hasErrors: false,
  pages: [],
};

const slice = createSlice({
  name: 'viewerPages',
  initialState,
  reducers: {
    onLoad: (state, { payload: data }: PayloadAction<ViewerPagesState>) => {
      state.pages = data.pages;
    },
  },
});

export default slice.reducer;

// Actions
export const { onLoad } = slice.actions;

// Selectors
export const viewerPagesSelector = (state: TRootState) =>
  state.viewerPages.pages;

const appEnv =
  (import.meta.env.VITE_TE_APP_ENV as EAppEnv) ?? EAppEnv.PRODUCTION;

// Thunks
export const requestViewerPages =
  (customerSignature?: string) => async (dispatch: TAppDispatch) => {
    if (!customerSignature) {
      return;
    }
    const url =
      getRedirectUri(
        EAppId.TE_VIEWER,
        removeCloudPrefix(customerSignature),
        appEnv
      ) + '/pages.json';
    try {
      const response = await axios<ResponseType>(url, {});
      dispatch(
        onLoad({ pages: createOptions(response.data), hasErrors: false })
      );
    } catch (e) {
      const errorDetail = {
        message: 'Failed to load pages from Viewer',
        description: 'URL ' + url + '\n. ' + e,
      };
      errorNotification(errorDetail.message, [errorDetail.description]);
    }
  };

export type ViewerOptions = {
  key: string;
  label: string;
  options?: ViewerPageOptions[];
};

export function createOptions(json: unknown): ViewerOptions[] | undefined {
  if (!isArray(json)) {
    return;
  }
  if (json.length === 0) {
    return;
  }

  const options = [];
  for (const entry of json) {
    if (isUndefined(entry.name)) {
      continue;
    }
    if (isUndefined(entry.pages)) {
      continue;
    }
    const path = `${entry.path}`;
    options.push({
      key: path,
      label: `${entry.name}`,
      options: makePageOptions(entry.pages, path),
    });
  }
  return options;
}

type ViewerPageOptions = {
  value: string;
  label: string;
  id: string;
  path: string;
  urlstart: string;
};

function makePageOptions(
  json: unknown,
  path: string
): ViewerPageOptions[] | undefined {
  if (!isArray(json)) {
    return;
  }
  if (json.length === 0) {
    return;
  }
  const options = [];
  for (const page of json) {
    if (page.type === 'WelcomeController') {
      continue;
    }
    if (page.type === 'SplitController') {
      continue;
    }

    options.push({
      value: `${path}${page.id}`,
      label: `${page.name}`,
      id: `${page.id}`,
      path: `${path}`,
      urlstart: `${page.urlstart}`,
    });
  }
  return options;
}
